import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, XCircle, Shield, GraduationCap, Calendar, Award, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CertificateVerify = () => {
  const { certificateId } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verify = async () => {
      try {
        const res = await fetch(`${API_URL}/api/training/certificates/${certificateId}/verify-public`);
        const data = await res.json();
        setResult(data);
      } catch {
        setResult({ valid: false, message: 'Unable to verify certificate' });
      } finally {
        setLoading(false);
      }
    };
    verify();
  }, [certificateId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex items-center justify-center p-4" data-testid="certificate-verify-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <Shield className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-xl font-bold text-slate-900">Certificate Verification</h1>
          <p className="text-sm text-slate-500 mt-1">Training Certificate Authenticity Check</p>
        </div>

        <Card className="shadow-lg border-0" data-testid="verification-result">
          <CardContent className="p-6">
            {result?.valid ? (
              <div className="space-y-5">
                <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-emerald-800" data-testid="valid-status">Valid Certificate</p>
                    <p className="text-xs text-emerald-600">This certificate is authentic and verified</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <GraduationCap className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-slate-500">Recipient</p>
                      <p className="font-medium text-slate-900" data-testid="cert-employee">{result.employee_name}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Award className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-slate-500">Course</p>
                      <p className="font-medium text-slate-900" data-testid="cert-course">{result.course_name}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Calendar className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-slate-500">Completed On</p>
                      <p className="font-medium text-slate-900" data-testid="cert-date">{result.completed_date}</p>
                    </div>
                  </div>
                  {result.score != null && (
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-slate-500">Score</p>
                        <Badge className="bg-indigo-100 text-indigo-700 mt-0.5" data-testid="cert-score">{result.score}%</Badge>
                      </div>
                    </div>
                  )}
                </div>

                <div className="pt-3 border-t">
                  <p className="text-xs text-slate-400">
                    Certificate ID: <span className="font-mono text-slate-600" data-testid="cert-id">{result.certificate_id}</span>
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Issued: {new Date(result.issued_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="font-semibold text-red-800 text-lg" data-testid="invalid-status">Invalid Certificate</h3>
                <p className="text-sm text-slate-500 mt-2">{result?.message || 'This certificate could not be verified.'}</p>
                <p className="text-xs text-slate-400 mt-3 font-mono">ID: {certificateId}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 mt-4">Powered by HRMS Training System</p>
      </div>
    </div>
  );
};

export default CertificateVerify;
